import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, ArrowLeft, Mail, KeyRound, Lock, RefreshCw, CheckCircle } from 'lucide-react';
import textoLogo from '../../assets/Texto logo.png';
import { API_BASE_URL } from '../../constants/api';
import { getFirstAccessibleRoute } from '../../utils/route-utils';
import { ClientesService } from '../../services/clientes/clientes.service';

interface LoginFormProps {
  onSubmit?: (data: { email: string; password: string }) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit }) => {
  const navigate = useNavigate();

  // Modo: login | forgot
  const [mode, setMode] = useState<'login' | 'forgot'>('login');

  // Login state
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Forgot password state
  const [forgotStep, setForgotStep] = useState<'email' | 'code' | 'success'>('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error en el login');
      }

      const response_data = await response.json();
      const data = response_data.data;

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('userData', JSON.stringify(data.usuario));

      const usuario = data.usuario;
      if (usuario.tipo === 'cliente' || usuario.role === 'cliente' || usuario.tipoUsuario === 'cliente') {
        navigate('/cliente');
      } else {
        const targetRoute = getFirstAccessibleRoute(usuario);
        navigate(targetRoute);
      }
    } catch (error: any) {
      console.error('Error en login:', error);
      setError(error.message || 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      setError('Ingresa tu correo electrónico');
      return;
    }
    setForgotLoading(true);
    setError(null);
    try {
      const response = await ClientesService.solicitarResetPassword(forgotEmail);
      if (response.success) {
        setForgotStep('code');
      } else {
        setError(response.message || 'Error al enviar el código');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al procesar la solicitud');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotCode.trim() || forgotCode.length !== 6) {
      setError('Ingresa el código de 6 dígitos');
      return;
    }
    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    setForgotLoading(true);
    setError(null);
    try {
      const response = await ClientesService.resetPassword(forgotEmail, forgotCode, newPassword);
      if (response.success) {
        setForgotStep('success');
      } else {
        setError(response.message || 'Error al restablecer la contraseña');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Código inválido o expirado');
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgot = () => {
    setMode('login');
    setForgotStep('email');
    setForgotEmail('');
    setForgotCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  return (
    <div className="w-full max-w-md bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 p-8 relative overflow-hidden">
      {/* Decoración de fondo sutil */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#4CAF50]/10 to-transparent rounded-bl-full" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-[#2196F3]/10 to-transparent rounded-tr-full" />

      {/* Header con logo de texto */}
      <div className="text-center mb-8 relative z-10">
        <div className="inline-block">
          <img src={textoLogo} alt="ConfiFarma Amiga" className="h-12 w-auto mx-auto" />
        </div>
        <p className="mt-3 text-sm font-medium text-gray-500">
          {mode === 'login' ? 'Accede a tu cuenta' : 'Recupera tu contraseña'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50/80 border border-red-200 rounded-xl flex items-start space-x-3 animate-shake">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* ===== LOGIN FORM ===== */}
      {mode === 'login' && (
        <form onSubmit={handleLoginSubmit} className="space-y-6">
          <div className="group">
            <label htmlFor="email" className="block text-sm font-medium mb-2 transition-colors text-gray-700">
              Correo electrónico
            </label>
            <div className="relative">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3.5 rounded-xl border-2 transition-all duration-300 bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20"
                placeholder="tu@email.com"
              />
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] transition-all duration-300 ${formData.email ? 'w-full' : 'w-0 group-focus-within:w-full'}`} />
            </div>
          </div>

          <div className="group">
            <label htmlFor="password" className="block text-sm font-medium mb-2 transition-colors text-gray-700">
              Contraseña
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3.5 pr-12 rounded-xl border-2 transition-all duration-300 bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-[#2196F3] transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] transition-all duration-300 ${formData.password ? 'w-full' : 'w-0 group-focus-within:w-full'}`} />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center cursor-pointer group">
              <input type="checkbox" className="w-4 h-4 text-[#4CAF50] border-gray-300 rounded focus:ring-[#4CAF50] cursor-pointer" />
              <span className="ml-2 text-sm text-gray-600 group-hover:text-[#2196F3] transition-colors">Recordarme</span>
            </label>
            <button
              type="button"
              onClick={() => { setMode('forgot'); setError(null); }}
              className="text-sm font-medium bg-gradient-to-r from-[#4CAF50] to-[#2196F3] bg-clip-text text-transparent hover:opacity-80 transition-opacity cursor-pointer"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl shadow-lg relative overflow-hidden group ${
              isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:from-[#43a047] hover:to-[#1e88e5]'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            {isLoading ? (
              <div className="flex items-center justify-center relative z-10">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Iniciando sesión...
              </div>
            ) : (
              <span className="relative z-10">Iniciar Sesión</span>
            )}
          </button>
        </form>
      )}

      {/* ===== FORGOT PASSWORD - STEP 1: EMAIL ===== */}
      {mode === 'forgot' && forgotStep === 'email' && (
        <form onSubmit={handleSendCode} className="space-y-6">
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Ingresa tu correo electrónico y te enviaremos un código de 6 dígitos para restablecer tu contraseña.
            </p>
            <label htmlFor="forgot-email" className="block text-sm font-medium mb-2 text-gray-700">
              Correo electrónico
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="forgot-email"
                type="email"
                required
                value={forgotEmail}
                onChange={(e) => { setForgotEmail(e.target.value); if (error) setError(null); }}
                className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 transition-all duration-300 bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20"
                placeholder="tu@email.com"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={forgotLoading}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl shadow-lg relative overflow-hidden group ${
              forgotLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:from-[#43a047] hover:to-[#1e88e5]'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            {forgotLoading ? (
              <div className="flex items-center justify-center relative z-10">
                <RefreshCw className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                Enviando código...
              </div>
            ) : (
              <span className="relative z-10 flex items-center justify-center gap-2">
                <Mail className="w-5 h-5" />
                Enviar código
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={resetForgot}
            className="w-full py-3 rounded-xl font-medium text-gray-600 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio de sesión
          </button>
        </form>
      )}

      {/* ===== FORGOT PASSWORD - STEP 2: CODE + NEW PASSWORD ===== */}
      {mode === 'forgot' && forgotStep === 'code' && (
        <form onSubmit={handleResetPassword} className="space-y-5">
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Ingresa el código de 6 dígitos que enviamos a <strong className="text-gray-900">{forgotEmail}</strong> y crea tu nueva contraseña.
            </p>
            <label htmlFor="forgot-code" className="block text-sm font-medium mb-2 text-gray-700">
              Código de verificación
            </label>
            <div className="relative">
              <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="forgot-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                value={forgotCode}
                onChange={(e) => { setForgotCode(e.target.value.replace(/\D/g, '')); if (error) setError(null); }}
                className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 transition-all duration-300 bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20 text-center tracking-[0.5em] text-xl font-bold"
                placeholder="000000"
              />
            </div>
          </div>

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium mb-2 text-gray-700">
              Nueva contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); if (error) setError(null); }}
                className="w-full pl-12 pr-12 py-3.5 rounded-xl border-2 transition-all duration-300 bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20"
                placeholder="Crea tu nueva contraseña"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-[#2196F3] transition-colors"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium mb-2 text-gray-700">
              Confirmar contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                minLength={6}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); if (error) setError(null); }}
                className="w-full pl-12 pr-12 py-3.5 rounded-xl border-2 transition-all duration-300 bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#2196F3] focus:ring-2 focus:ring-[#2196F3]/20"
                placeholder="Repite tu contraseña"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-[#2196F3] transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={forgotLoading}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl shadow-lg relative overflow-hidden group ${
              forgotLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:from-[#43a047] hover:to-[#1e88e5]'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            {forgotLoading ? (
              <div className="flex items-center justify-center relative z-10">
                <RefreshCw className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                Actualizando...
              </div>
            ) : (
              <span className="relative z-10 flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Actualizar contraseña
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={resetForgot}
            className="w-full py-3 rounded-xl font-medium text-gray-600 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio de sesión
          </button>
        </form>
      )}

      {/* ===== FORGOT PASSWORD - STEP 3: SUCCESS ===== */}
      {mode === 'forgot' && forgotStep === 'success' && (
        <div className="text-center space-y-6">
          <div className="w-20 h-20 bg-gradient-to-br from-[#4CAF50] to-[#2196F3] rounded-full flex items-center justify-center mx-auto shadow-lg">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Contraseña actualizada!</h3>
            <p className="text-gray-600">
              Tu contraseña ha sido restablecida exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
            </p>
          </div>
          <button
            onClick={resetForgot}
            className="w-full py-4 px-6 rounded-xl font-semibold text-white bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:from-[#43a047] hover:to-[#1e88e5] transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl shadow-lg"
          >
            Iniciar sesión
          </button>
        </div>
      )}

      {/* Back to home */}
      {mode === 'login' && (
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#2196F3] transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Volver al inicio
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <p className="text-xs text-center text-gray-400">
          Tu farmacia de confianza<br />
          <span className="text-[#4CAF50]">ConfiFarma Amiga</span> © 2026
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
